// src/components/shared/Navigation.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, User, Users, Heart, Trophy, BarChart2, Star } from 'lucide-react';
import FPLLogo from './FPLLogo';

/**
 * Reusable navigation buttons component
 */
export const NavigationButtons = ({
  backPath = '/journey-selector',
  forwardPath = '/my-journey',
  backText = 'Back to Journey Selector',
  forwardText = 'Go to My Journey',
  centerButton = null,
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex justify-between items-center w-full">
      <button
        onClick={() => navigate(backPath)}
        className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid var(--c-border)',
          color: 'var(--c-grey)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-grey)'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
      >
        <ChevronLeft size={16} />
        {backText}
      </button>

      {centerButton}

      <button
        onClick={() => navigate(forwardPath)}
        className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
        style={{ backgroundColor: 'var(--c-blue)' }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        {forwardText}
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

const NAV_ITEMS = [
  { icon: <User size={17} />, label: 'My Journey', path: '/my-journey' },
  { icon: <Heart size={17} />, label: 'Transfer Regrets', path: '/transfer-regrets' },
  { icon: <Trophy size={17} />, label: 'Captain Hindsight', path: '/captain-hindsight' },
  { icon: <Star size={17} />, label: 'Differential Gems', path: '/differential-king' },
  { icon: <Trophy size={17} />, label: 'League Awards', path: '/league-awards' },
  { icon: <Users size={17} />, label: 'League Overview', path: '/league-overview' },
  { icon: <BarChart2 size={17} />, label: 'Journey Selector', path: '/journey-selector' },
];

/**
 * App-wide slide-out menu component
 */
export const AppMenu = ({ title = 'FPL Wrapped' }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const navigateTo = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Top nav bar */}
      <div className="absolute top-0 left-0 w-full p-4 flex justify-between items-center z-10">
        <FPLLogo size="icon" colour="light" className="h-7" />
        <button
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid var(--c-border)', color: 'white' }}
        >
          <span className="flex flex-col gap-1 w-4">
            <span className="block w-full h-0.5 bg-current rounded" />
            <span className="block w-full h-0.5 bg-current rounded" />
            <span className="block w-3 h-0.5 bg-current rounded" />
          </span>
          Menu
        </button>
      </div>

      {/* Backdrop */}
      {menuOpen && (
        <div
          className="absolute inset-0 z-10"
          style={{ backgroundColor: 'rgba(0, 0, 6, 0.6)' }}
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Slide-out panel */}
      <div
        className="absolute top-0 right-0 h-full w-72 transform transition-transform duration-300 z-20"
        style={{
          backgroundColor: 'var(--c-surface-1)',
          borderLeft: '1px solid var(--c-border)',
          transform: menuOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <div
          className="p-4 flex justify-between items-center"
          style={{ borderBottom: '1px solid var(--c-border)' }}
        >
          <FPLLogo size="full" colour="light" className="h-6" />
          <button
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider mb-3 px-4"
            style={{ color: 'var(--c-grey)' }}>Navigation</p>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.path}
              onClick={() => navigateTo(item.path)}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-left transition-all"
              style={{ color: 'var(--c-grey)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--c-surface-2)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--c-grey)';
              }}
            >
              <span style={{ color: 'var(--c-blue)' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* Footer branding */}
        <div
          className="absolute bottom-0 left-0 right-0 p-4 text-xs text-center"
          style={{ color: 'var(--c-grey)', borderTop: '1px solid var(--c-border)' }}
        >
          FPL Wrapped · 2025/26 Season
        </div>
      </div>
    </>
  );
};

/**
 * Page container
 */
export const PageContainer = ({ children, title, titleColor = 'text-white' }) => (
  <div
    className="h-full flex flex-col items-center justify-between py-6 text-white px-4"
    style={{ background: 'linear-gradient(135deg, #000006 0%, #0a1628 100%)' }}
  >
    {title && <h2 className={`text-2xl font-bold ${titleColor} mb-2`}>{title}</h2>}
    {children}
  </div>
);

/**
 * Card
 */
export const Card = ({ children, className = '', maxWidth = 'max-w-sm' }) => (
  <div
    className={`w-full ${maxWidth} rounded-2xl overflow-hidden p-6 text-white ${className}`}
    style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
  >
    {children}
  </div>
);

/**
 * Stat display component
 */
export const StatDisplay = ({ label, value, color = 'var(--c-blue)' }) => (
  <div
    className="p-5 rounded-xl animate-pop-in"
    style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
  >
    <div className="text-3xl font-bold mb-1" style={{ color }}>{value}</div>
    <div className="text-xs" style={{ color: 'var(--c-grey)' }}>{label}</div>
  </div>
);

/**
 * Progress bar component
 */
export const ProgressBar = ({
  value,
  max,
  height = 'h-2',
  animate = true,
  color = 'var(--c-blue)',
  className = '',
}) => {
  const percentage = Math.min(Math.round((value / max) * 100), 100);

  return (
    <div className={`relative ${height} rounded-full overflow-hidden ${className}`}
      style={{ backgroundColor: 'var(--c-surface-3)' }}>
      <div
        className={`absolute h-full rounded-full ${animate ? 'transition-all duration-1000 ease-out' : ''}`}
        style={{ width: `${percentage}%`, backgroundColor: color }}
      />
    </div>
  );
};
